// Auth surface — login + register + logout + current-user lookup.
// State is owned by TanStack Query under the `auth.me` key; calling
// any of these mutators refreshes the cache so route guards
// re-evaluate.
//
// Login is two-step when the account has an active MFA factor:
//   1. POST /api/auth/login {email, password}
//        - 200 + LoginResponse → cookie minted now
//        - 428 + LoginMFAChallenge → call completeMFA next
//   2. POST /api/auth/login/mfa {challenge_id, factor_id, code}
//        - 200 + LoginResponse → cookie minted, csrf_token returned
import { api, setCSRF } from "./api";
import { setSessionScope } from "./session";
import type { AuthMe, RegisterResponse } from "./types";

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResponse {
  user_id: string;
  email: string;
  role: string;
  csrf_token: string;
  expires_at: string;
}

// LoginFactorOption is one entry in the MFA challenge's factor picker.
export interface LoginFactorOption {
  factor_id: string;
  kind: string;
  friendly_name: string;
}

export interface LoginMFAChallenge {
  needs_mfa: true;
  challenge_id: string;
  user_id: string;
  email: string;
  factors: LoginFactorOption[];
}

export type LoginResult =
  | { kind: "success"; data: LoginResponse }
  | { kind: "needs_mfa"; challenge: LoginMFAChallenge };

export async function login(input: LoginInput): Promise<LoginResult> {
  const res = (await api<LoginResponse | LoginMFAChallenge>("/api/auth/login", {
    method: "POST",
    body: input,
    skipCSRF: true,
    expectMFAChallenge: true,
  })) as LoginResponse | LoginMFAChallenge;
  if ((res as LoginMFAChallenge).needs_mfa) {
    return { kind: "needs_mfa", challenge: res as LoginMFAChallenge };
  }
  const ok = res as LoginResponse;
  setCSRF(ok.csrf_token ?? null);
  return { kind: "success", data: ok };
}

export interface CompleteMFAInput {
  challenge_id: string;
  factor_id: string;
  code: string;
}

export async function completeMFA(input: CompleteMFAInput): Promise<LoginResponse> {
  const res = await api<LoginResponse>("/api/auth/login/mfa", {
    method: "POST",
    body: input,
    skipCSRF: true,
  });
  setCSRF(res.csrf_token ?? null);
  return res;
}

export interface RegisterInput {
  email: string;
  password: string;
  display_name?: string;
}

export async function register(input: RegisterInput): Promise<RegisterResponse> {
  return api<RegisterResponse>("/api/auth/register", {
    method: "POST",
    body: input,
    skipCSRF: true,
  });
}

// confirmEmail redeems the magic-link token. The gateway verifies the
// email AND mints a session (verify + auto-login), so the response is the
// same LoginResponse shape as a successful sign-in — we stash the CSRF
// token exactly like login does.
export async function confirmEmail(token: string): Promise<LoginResponse> {
  const res = await api<LoginResponse>("/api/auth/confirm-email", {
    method: "POST",
    body: { token },
    skipCSRF: true,
  });
  setCSRF(res.csrf_token ?? null);
  return res;
}

// resendConfirmation requests a fresh confirmation email. Enumeration-safe
// on the backend (always succeeds for unknown / already-verified
// addresses), so callers show a single generic "sent" message.
export async function resendConfirmation(email: string): Promise<void> {
  await api("/api/auth/resend-confirmation", {
    method: "POST",
    body: { email },
    skipCSRF: true,
  });
}

// requestPasswordReset emails a reset magic link. Enumeration-safe on the
// backend (always succeeds), so callers show a single generic message.
export async function requestPasswordReset(email: string): Promise<void> {
  await api("/api/auth/forgot-password", {
    method: "POST",
    body: { email },
    skipCSRF: true,
  });
}

// resetPassword sets a new password from a reset token. No session is
// returned (redirect-to-login) — the caller sends the user to sign in.
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api("/api/auth/reset-password", {
    method: "POST",
    body: { token, new_password: newPassword },
    skipCSRF: true,
  });
}

export async function logout(): Promise<void> {
  try {
    await api("/api/auth/logout", { method: "POST" });
  } finally {
    setCSRF(null);
  }
}

// --- OAuth / social sign-in ---
//
// Starting OAuth is a full-page navigation: the gateway 302s to the
// provider, the user authenticates there, and the provider returns to
// the gateway callback which bounces back to /auth/callback with the
// outcome. So these helpers navigate the browser rather than fetch.

export type OAuthIntent = "login" | "change_email";

export function oauthStartUrl(
  provider: string,
  intent: OAuthIntent = "login",
  redirect?: string,
): string {
  const q = new URLSearchParams({ intent });
  if (redirect) q.set("redirect", redirect);
  return `/api/auth/oauth/${provider.toLowerCase()}/start?${q.toString()}`;
}

// startOAuth navigates to begin sign-in (intent=login) or, for a social-only
// account changing its email from settings, to re-authenticate and swap the
// provider account (intent=change_email).
export function startOAuth(
  provider: string,
  intent: OAuthIntent = "login",
  redirect?: string,
): void {
  window.location.assign(oauthStartUrl(provider, intent, redirect));
}

// --- Connected providers (identities) ---

export interface IdentityItem {
  identity_id: string;
  provider_kind: string; // EMAIL / GOOGLE / GITHUB / APPLE
  is_primary: boolean;
  email?: string;
  email_verified?: boolean;
  status?: string; // PENDING_VERIFICATION for an unconfirmed email bind
  linked_at: string;
  last_used_at?: string;
}

export async function listIdentities(): Promise<IdentityItem[]> {
  const res = await api<{ identities?: IdentityItem[] } | IdentityItem[]>(
    "/api/me/identities",
  );
  return Array.isArray(res) ? res : (res.identities ?? []);
}

// changeEmail starts moving a password account's canonical email to a new
// address. The gateway emails a verification link to the new address; the
// change applies only when the user clicks it (the same /confirm-email
// redeem path, dispatched by the EMAIL_CHANGE purpose). Social-only accounts
// change email through the provider OAuth flow instead (startOAuth …
// change_email).
export async function changeEmail(newEmail: string): Promise<void> {
  await api("/api/me/email", { method: "POST", body: { new_email: newEmail } });
}

// --- Account deletion ---

export interface DeletionBlocker {
  type: string;
  count?: number;
  amount?: string;
  message: string;
  deep_link?: string;
}

export interface DeletionEligibility {
  eligible: boolean;
  blockers: DeletionBlocker[];
}

export async function deletionEligibility(): Promise<DeletionEligibility> {
  return api<DeletionEligibility>("/api/me/deletion-eligibility");
}

// deleteAccount soft-deletes the account. The gateway re-checks
// eligibility server-side and returns 409 with blockers if it is no
// longer eligible. On success the session is ended; the caller routes the
// (now anonymous) user back to the landing page.
export async function deleteAccount(reason?: string): Promise<void> {
  await api("/api/me/delete", { method: "POST", body: { reason: reason ?? "" } });
  setCSRF(null);
}

// addEmailCredential starts adding an email+password sign-in method to a
// social-only account (e.g. Google-only). The gateway emails a verification
// link; the credential is created only when the user clicks it (reusing the
// confirm-email flow), so an unverified address never yields a usable login.
export async function addEmailCredential(input: { email: string; password: string }): Promise<void> {
  await api("/api/me/email-credential", { method: "POST", body: input });
}

// resendEmailCredential re-sends the pending email-bind verification link,
// reusing the stored address + password (no re-entry).
export async function resendEmailCredential(): Promise<void> {
  await api("/api/me/email-credential/resend", { method: "POST" });
}

// fetchMeOrNull is used by the root route loader to decide whether to
// render header / portfolio links in their signed-in state. It MUST
// not throw for any condition that simply means "we can't determine
// auth state right now" — otherwise the throw propagates to TanStack
// Router's error boundary and the user sees a full-page red wall
// instead of an app that just hasn't recognised them yet.
//
// Swallowed (return null, render as anonymous):
//   - 401 / 403  — real "no session" or "session rejected" signal.
//   - 429        — gateway rate-limit; cache will refetch on next
//                  query tick once the limiter resets.
//   - 5xx        — transient backend / store blip. Common during
//                  redis-cluster slot rebalance after a cluster
//                  restart (the rate-limit store fails closed, so
//                  every /me round-trips a 500 for a few seconds).
//
// Re-thrown:
//   - Network errors (no `status` on the thrown value) — the caller's
//     retry policy decides what to do; eventually surfaces through
//     the route-level errorComponent if every retry fails.
//   - Other 4xx (404, 4xx validation) — real bug; we want it visible.
export async function fetchMeOrNull(): Promise<AuthMe | null> {
  try {
    const me = await api<AuthMe>("/api/auth/me");
    // Persist the session CSRF token on every successful me-fetch. Email/
    // password login stashes it from the login response, but OAuth sign-in
    // lands via a browser redirect (no JSON body), so this is the only place
    // the token gets populated for Google sign-in. Without it, every
    // state-changing request 403s "missing or invalid CSRF token".
    setCSRF(me.csrf_token ?? null);
    // The private cache scope follows the session this answer describes
    // (SM-765) — set before the query resolves, so the re-render it
    // triggers keys every private query in the new scope.
    setSessionScope(me);
    return me;
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === undefined) throw err;
    if (status === 401 || status === 403) {
      setSessionScope(null);
      return null;
    }
    if (status === 429 || (status >= 500 && status < 600)) {
      // Render as anonymous now; the next query refetch (or a manual
      // route reload) will retry against the recovered gateway.
      console.warn(
        `[auth] /me transient ${status}; rendering anonymous until cache refresh`,
      );
      setSessionScope(null);
      return null;
    }
    throw err;
  }
}
